<template>
  <div class="ado-page">
    <div class="pg-title-row">
      <div>
        <h1 class="pg-title">Admin Data Operations</h1>
        <p class="pg-title-sub">Find a race, correct results, inspect runners, and upload recordings from one controlled admin workspace.</p>
      </div>
      <button class="ado-ghost-btn" type="button" @click="openAdminTools">
        <svg viewBox="0 0 24 24" class="ado-btn-icon"><path d="M12 15.5A3.5 3.5 0 1 0 12 8a3.5 3.5 0 0 0 0 7.5Z"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1A2 2 0 1 1 4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.6-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7A2 2 0 1 1 7 4.2l.1.1a1.7 1.7 0 0 0 1.9.3h.1A1.7 1.7 0 0 0 10 3V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1A2 2 0 1 1 19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9v.1A1.7 1.7 0 0 0 21 10h.1a2 2 0 1 1 0 4H21a1.7 1.7 0 0 0-1.6 1Z"/></svg>
        Admin Tools
      </button>
    </div>

    <div class="ado-command-strip">
      <div class="ado-command-item ado-command-item-active">
        <span class="ado-command-kicker">Active race</span>
        <strong class="ado-command-value">{{ selectedRace ? selectedRace.id + ' - ' + selectedRace.name : 'No race selected' }}</strong>
      </div>
      <div class="ado-command-item">
        <span class="ado-command-kicker">Loaded results</span>
        <strong class="ado-command-value">{{ raceResults.length }}</strong>
      </div>
      <div class="ado-command-item">
        <span class="ado-command-kicker">Runner context</span>
        <strong class="ado-command-value">{{ selectedRunner ? selectedRunner.id + ' - ' + selectedRunner.firstName : 'Search by runner id' }}</strong>
      </div>
    </div>

    <div class="ado-workbench">
      <section class="ado-card ado-race-card">
        <div class="ado-card-header ado-card-header-compact">
          <div class="ado-header-left">
            <span class="ado-icon-pill">
              <svg viewBox="0 0 24 24" class="ado-icon"><path d="M8 2v4"/><path d="M16 2v4"/><path d="M3 10h18"/><path d="M5 4h14a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z"/></svg>
            </span>
            <div>
              <h2 class="ado-card-title">Race Sessions</h2>
              <p class="ado-card-sub">Find sessions by date or search and open result correction tools.</p>
            </div>
          </div>
        </div>

        <div class="ado-filter-panel">
          <label class="ado-field">
            <span class="ado-label">Search</span>
            <input v-model="raceSearch" class="ado-input" type="text" placeholder="race id or description" @keyup.enter="searchRaces">
          </label>
          <div class="ado-date-range-control">
            <span class="ado-label">Date range</span>
            <button class="ado-range-button" type="button" @click="toggleDatePicker">
              <span>
                <strong>{{ dateRangeLabel }}</strong>
                <small>{{ dateFilter.rangeStart === dateFilter.rangeEnd ? 'Single day' : 'Range' }}</small>
              </span>
              <svg viewBox="0 0 24 24" class="ado-icon"><path d="M8 2v4"/><path d="M16 2v4"/><path d="M3 10h18"/><path d="M5 4h14a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z"/></svg>
            </button>
            <div v-if="datePickerOpen" class="ado-calendar-popover">
              <div class="ado-calendar-presets">
                <button class="ado-date-chip" type="button" @click="setDatePreset('today')">Today</button>
                <button class="ado-date-chip" type="button" @click="setDatePreset('yesterday')">Yesterday</button>
                <button class="ado-date-chip" type="button" @click="setDatePreset('last7')">Last 7 days</button>
              </div>
              <div class="ado-calendar-head">
                <button class="ado-icon-btn" type="button" title="Previous month" @click="shiftCalendarMonth(-1)">
                  <svg viewBox="0 0 24 24" class="ado-btn-icon"><path d="m15 18-6-6 6-6"/></svg>
                </button>
                <strong>{{ calendarMonthLabel }}</strong>
                <button class="ado-icon-btn" type="button" title="Next month" @click="shiftCalendarMonth(1)">
                  <svg viewBox="0 0 24 24" class="ado-btn-icon"><path d="m9 18 6-6-6-6"/></svg>
                </button>
              </div>
              <div class="ado-calendar-weekdays">
                <span v-for="weekday in calendarWeekdays" :key="weekday">{{ weekday }}</span>
              </div>
              <div class="ado-calendar-grid">
                <button
                  v-for="day in calendarDays"
                  :key="day.key"
                  class="ado-calendar-day"
                  :class="{
                    'ado-calendar-day-muted': !day.inMonth,
                    'ado-calendar-day-selected': isCalendarBoundary(day.value),
                    'ado-calendar-day-range': isCalendarInRange(day.value),
                    'ado-calendar-day-today': day.value === todayInput,
                  }"
                  type="button"
                  @click="selectCalendarDay(day.value)"
                >
                  {{ day.label }}
                </button>
              </div>
              <div class="ado-calendar-footer">
                <span>{{ pendingDateRangeLabel }}</span>
                <button class="ado-primary-btn" type="button" @click="applyDateRange">
                  <svg viewBox="0 0 24 24" class="ado-btn-icon"><path d="M21 21l-4.3-4.3"/><circle cx="11" cy="11" r="8"/></svg>
                  Apply & Load
                </button>
              </div>
            </div>
          </div>
          <button class="ado-ghost-btn ado-fluid-btn" type="button" @click="searchRaces">Search Races</button>
        </div>

        <div v-if="raceLoading" class="ado-skeleton-list">
          <div v-for="idx in 4" :key="idx" class="ado-skeleton-row">
            <span class="sk-shimmer ado-sk-title"></span>
            <span class="sk-shimmer ado-sk-line"></span>
            <span class="sk-shimmer ado-sk-pill"></span>
          </div>
        </div>
        <div v-else class="ado-race-list">
          <button
            v-for="race in races"
            :key="race.id"
            class="ado-race-option"
            :class="{ 'ado-race-option-selected': selectedRace && selectedRace.id === race.id }"
            type="button"
            @click="selectRace(race)"
          >
            <span class="ado-race-main">
              <strong class="ado-race-title">{{ race.id }} - {{ race.name }}</strong>
              <span class="ado-race-meta">{{ race.trainer && race.trainer.firstName ? race.trainer.firstName : 'Trainer not set' }}</span>
            </span>
            <span class="ado-race-stats">
              <span class="ado-stat-pill">{{ race.duration || '-' }}</span>
              <span class="ado-stat-pill">{{ race.totalAmountRunners || 0 }} runners</span>
            </span>
            <span class="ado-race-foot">
              <span>{{ formatDate(race.rawStartDate || race.rawDate) || 'No date' }}</span>
              <a v-if="race.liveClassRecordedUrl" class="ado-mini-link" :href="race.liveClassRecordedUrl" target="_blank" rel="noopener" @click.stop>Recording</a>
            </span>
          </button>
          <div v-if="races.length === 0" class="ado-empty-state">No races loaded for this filter.</div>
        </div>
      </section>

      <section class="ado-card ado-results-card">
        <div class="ado-card-header ado-card-header-spread">
          <div class="ado-header-left">
            <span class="ado-icon-pill">
              <svg viewBox="0 0 24 24" class="ado-icon"><path d="M8 21h8"/><path d="M12 17v4"/><path d="M7 4h10v5a5 5 0 0 1-10 0V4Z"/><path d="M5 6H3a4 4 0 0 0 4 4"/><path d="M19 6h2a4 4 0 0 1-4 4"/></svg>
            </span>
            <div>
              <h2 class="ado-card-title">Race Results</h2>
              <p class="ado-card-sub">{{ selectedRace ? selectedRace.id + ' - ' + selectedRace.name : 'Select a race to load result rows.' }}</p>
            </div>
          </div>
          <div class="ado-actions ado-results-actions" v-if="selectedRace">
            <button class="ado-ghost-btn" type="button" @click="openBulkMedalDialog()">Bulk Medal</button>
            <button class="ado-primary-btn" type="button" @click="openResultDialog()">
              <svg viewBox="0 0 24 24" class="ado-btn-icon"><path d="M12 5v14"/><path d="M5 12h14"/></svg>
              Add Result
            </button>
          </div>
        </div>

        <div v-if="selectedRace" class="ado-selected-race-panel">
          <div class="ado-race-summary-item">
            <span class="ado-command-kicker">Duration</span>
            <strong class="ado-command-value">{{ selectedRace.duration || '-' }}</strong>
          </div>
          <div class="ado-race-summary-item">
            <span class="ado-command-kicker">Runners</span>
            <strong class="ado-command-value">{{ selectedRace.totalAmountRunners || 0 }}</strong>
          </div>
          <div class="ado-race-summary-item">
            <span class="ado-command-kicker">Start</span>
            <strong class="ado-command-value">{{ formatDate(selectedRace.rawStartDate || selectedRace.rawDate) || '-' }}</strong>
          </div>
        </div>

        <div v-if="selectedRace" class="ado-toolbar ado-result-toolbar">
          <label class="ado-field">
            <span class="ado-label">Runner Filter</span>
            <input v-model="resultSearch" class="ado-input" type="text" placeholder="runner id" @keyup.enter="loadRaceResults(true)">
          </label>
          <button class="ado-primary-btn" type="button" @click="loadRaceResults(true)">Search Results</button>
          <button class="ado-ghost-btn" type="button" @click="clearResultFilter">Clear</button>
        </div>

        <div v-if="resultLoading && raceResults.length === 0" class="ado-skeleton-list">
          <div v-for="idx in 4" :key="idx" class="ado-skeleton-row">
            <span class="sk-shimmer ado-sk-title"></span>
            <span class="sk-shimmer ado-sk-line"></span>
            <span class="sk-shimmer ado-sk-pill"></span>
          </div>
        </div>
        <div v-else class="ado-table-wrap">
          <table class="ado-table">
            <thead>
              <tr>
                <th class="ado-th">Runner</th>
                <th class="ado-th">Place</th>
                <th class="ado-th">Distance</th>
                <th class="ado-th">Created</th>
                <th class="ado-th">Medals</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="result in raceResults" :key="result.id" class="ado-result-row">
                <td class="ado-td">
                  <button class="ado-link-btn" type="button" @click="openResultDialog(result)">{{ result.runner && result.runner.id }} - {{ result.runner && result.runner.firstName }}</button>
                </td>
                <td class="ado-td ado-rank-cell">{{ result.runnerPlace || '-' }}</td>
                <td class="ado-td">{{ result.distanceLabel || result.distance }}</td>
                <td class="ado-td ado-muted">{{ formatDate(result.rawCreatedDate) }}</td>
                <td class="ado-td">
                  <button
                    v-for="entry in medalsForRunner(result.runner && result.runner.id)"
                    :key="`${result.runner && result.runner.id}-${entry.medal && entry.medal.id}`"
                    class="ado-medal-chip ado-medal-chip-button"
                    type="button"
                    @click="openBulkMedalDialog(result.runner && result.runner.id, entry.medal, 1)"
                  >
                    {{ entry.medal && entry.medal.badge }}
                  </button>
                  <button class="ado-mini-action" type="button" @click="openBulkMedalDialog(result.runner && result.runner.id)">Edit</button>
                </td>
              </tr>
              <tr v-if="raceResults.length === 0">
                <td colspan="5" class="ado-empty">{{ selectedRace ? 'No race results loaded.' : 'Select a race first.' }}</td>
              </tr>
            </tbody>
          </table>
          <button v-if="resultPageModel && resultPageModel.last === false" class="ado-ghost-btn ado-load-more" type="button" :disabled="resultLoading" @click="loadNextResultPage">
            Load page {{ resultPage + 2 }} of {{ resultPageModel.totalPages }}
          </button>
        </div>
      </section>
    </div>

    <modal name="ado-admin-tools-dialog" class="ado-modal" :adaptive="true" :scrollable="true" height="auto" width="94%" :maxWidth="980" @closed="handleAdminToolsClosed">
      <div class="ado-dialog ado-tools-dialog">
        <div class="ado-dialog-header">
          <span class="ado-icon-pill"><svg viewBox="0 0 24 24" class="ado-icon"><path d="M12 15.5A3.5 3.5 0 1 0 12 8a3.5 3.5 0 0 0 0 7.5Z"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1A2 2 0 1 1 4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.6-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7A2 2 0 1 1 7 4.2l.1.1a1.7 1.7 0 0 0 1.9.3h.1A1.7 1.7 0 0 0 10 3V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1A2 2 0 1 1 19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9v.1A1.7 1.7 0 0 0 21 10h.1a2 2 0 1 1 0 4H21a1.7 1.7 0 0 0-1.6 1Z"/></svg></span>
          <div>
            <h3 class="ado-dialog-title">Admin Tools</h3>
            <p class="ado-dialog-sub">Use runner maintenance and recording utilities without leaving result corrections.</p>
          </div>
          <button class="ado-close-btn" type="button" title="Close" @click="closeAdminTools">
            <svg viewBox="0 0 24 24" class="ado-btn-icon"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </button>
        </div>
        <div class="ado-dialog-body ado-tools-grid">
      <section class="ado-tool-panel">
        <div class="ado-card-header ado-card-header-spread">
          <div class="ado-header-left">
            <span class="ado-icon-pill">
              <svg viewBox="0 0 24 24" class="ado-icon"><path d="M20 21a8 8 0 0 0-16 0"/><circle cx="12" cy="7" r="4"/></svg>
            </span>
            <div>
              <h2 class="ado-card-title">Runner Tools</h2>
              <p class="ado-card-sub">Search, inspect analytics, reset distance, and manage promo codes.</p>
            </div>
          </div>
          <button class="ado-ghost-btn" type="button" @click="openBulkPromoDialog">Bulk Promo</button>
        </div>

        <div class="ado-toolbar ado-toolbar-stack">
          <label class="ado-field">
            <span class="ado-label">Runner Search</span>
            <input v-model="runnerSearch" class="ado-input" type="text" placeholder="runner id" @keyup.enter="searchRunners">
          </label>
          <button class="ado-primary-btn" type="button" @click="searchRunners">Search Runner</button>
        </div>

        <div v-if="runnerLoading" class="ado-skeleton-list">
          <div class="ado-skeleton-row">
            <span class="sk-shimmer ado-sk-title"></span>
            <span class="sk-shimmer ado-sk-line"></span>
          </div>
        </div>
        <div v-else>
          <div v-for="runner in runners" :key="runner.id" class="ado-runner-row">
            <button class="ado-runner-select" type="button" @click="openRunnerActions(runner.id)">
              <span>
                <strong>{{ runner.id }} - {{ runner.firstName }}</strong>
                <small>Open maintenance actions</small>
              </span>
              <svg viewBox="0 0 24 24" class="ado-btn-icon"><path d="m9 18 6-6-6-6"/></svg>
            </button>
          </div>
          <div v-if="runners.length === 0" class="ado-empty-state">Search a runner id to open analytics and promo-code tools.</div>
        </div>
      </section>

      <section class="ado-tool-panel">
        <div class="ado-card-header ado-card-header-compact">
          <div class="ado-header-left">
            <span class="ado-icon-pill">
              <svg viewBox="0 0 24 24" class="ado-icon"><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/><path d="M7 9l5-5 5 5"/><path d="M12 4v12"/></svg>
            </span>
            <div>
              <h2 class="ado-card-title">Recording Uploader</h2>
              <p class="ado-card-sub">Upload MP3 files and keep a local URL history.</p>
            </div>
          </div>
        </div>

        <label class="ado-file-zone">
          <input ref="mp3Input" class="ado-file-input" type="file" accept=".mp3,audio/mpeg" multiple @change="handleFiles">
          <span class="ado-file-zone-title">Select MP3 files</span>
          <span class="ado-file-zone-sub">{{ uploadFiles.length ? uploadFiles.length + ' file(s) ready' : 'Drag-style picker for recordings' }}</span>
        </label>
        <div v-for="file in uploadFiles" :key="file._key" class="ado-upload-row">
          <span>{{ file.name }}{{ file.extension }} · {{ file.sizeLabel }}</span>
          <span>{{ file.progress || 0 }}%</span>
        </div>
        <div class="ado-actions">
          <button class="ado-primary-btn" type="button" :disabled="uploadLoading || uploadFiles.length === 0" @click="uploadAllFiles">Upload</button>
          <button class="ado-ghost-btn" type="button" @click="clearUploadHistory">Clear History</button>
        </div>
        <div class="ado-history">
          <div v-for="entry in uploadHistory" :key="entry.url" class="ado-promo-row">
            <span>{{ entry.name }}</span>
            <button class="ado-ghost-btn" type="button" @click="copyUrl(entry.url)">Copy URL</button>
          </div>
        </div>
      </section>
        </div>
      </div>
    </modal>

    <modal name="ado-runner-actions-dialog" class="ado-modal" :adaptive="true" :scrollable="true" height="auto" width="90%" :maxWidth="760">
      <div class="ado-dialog">
        <div class="ado-dialog-header">
          <span class="ado-icon-pill"><svg viewBox="0 0 24 24" class="ado-icon"><path d="M20 21a8 8 0 0 0-16 0"/><circle cx="12" cy="7" r="4"/></svg></span>
          <div>
            <h3 class="ado-dialog-title">Runner Actions</h3>
            <p class="ado-dialog-sub">{{ selectedRunner ? selectedRunner.id + ' - ' + selectedRunner.firstName : 'Loading runner context' }}</p>
          </div>
          <button class="ado-close-btn" type="button" title="Close" @click="$modal.hide('ado-runner-actions-dialog')">
            <svg viewBox="0 0 24 24" class="ado-btn-icon"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </button>
        </div>
        <div class="ado-dialog-body">
          <div v-if="runnerLoading && !selectedRunner" class="ado-skeleton-list">
            <div class="ado-skeleton-row">
              <span class="sk-shimmer ado-sk-title"></span>
              <span class="sk-shimmer ado-sk-line"></span>
              <span class="sk-shimmer ado-sk-pill"></span>
            </div>
          </div>
          <div v-else-if="selectedRunner" class="ado-runner-actions-panel">
            <div class="ado-detail-title">{{ selectedRunner.id }} - {{ selectedRunner.firstName }}</div>
            <div v-if="runnerAnalytics" class="ado-kpi-grid">
              <div class="ado-kpi"><span class="ado-kpi-label">Total completed</span><strong class="ado-kpi-value">{{ runnerAnalytics.totalCompletedRuns }}</strong></div>
              <div class="ado-kpi"><span class="ado-kpi-label">Total distance</span><strong class="ado-kpi-value">{{ runnerAnalytics.totalDistancePerfomed }}</strong></div>
              <div class="ado-kpi"><span class="ado-kpi-label">Sum distance</span><strong class="ado-kpi-value">{{ runnerAnalytics.sumTotalDistancePerfomed }}</strong></div>
              <div class="ado-kpi"><span class="ado-kpi-label">Last 30 days</span><strong class="ado-kpi-value">{{ runnerAnalytics.totalRacesLast30days }}</strong></div>
            </div>
            <div class="ado-runner-action-strip">
              <button class="ado-ghost-btn" type="button" @click="resetRunnerDistance">
                <svg viewBox="0 0 24 24" class="ado-btn-icon"><path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v6h6"/></svg>
                Reset Distance
              </button>
              <button class="ado-ghost-btn" type="button" @click="resetRunnerPassword">
                <svg viewBox="0 0 24 24" class="ado-btn-icon"><rect x="3" y="11" width="18" height="10" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                Reset Password
              </button>
            </div>
            <div class="ado-promo-manager">
              <div class="ado-section-heading">
                <strong>Promo codes</strong>
                <span>{{ selectedRunner.memberShip && selectedRunner.memberShip.promoCodeList ? selectedRunner.memberShip.promoCodeList.length : 0 }} active</span>
              </div>
              <div class="ado-toolbar ado-toolbar-stack">
                <label class="ado-field">
                  <span class="ado-label">Promo code id or code</span>
                  <input v-model="runnerPromoCode" class="ado-input" type="text" placeholder="promo id or code">
                </label>
                <button class="ado-primary-btn" type="button" @click="addPromoCodeToRunner">Add Promo Code</button>
              </div>
              <div v-if="selectedRunner.memberShip && selectedRunner.memberShip.promoCodeList && selectedRunner.memberShip.promoCodeList.length" class="ado-promo-list">
                <div v-for="promo in selectedRunner.memberShip.promoCodeList" :key="promo.id" class="ado-promo-row">
                  <span>{{ promo.id }} - {{ promo.code }}</span>
                  <button class="ado-icon-btn" type="button" title="Remove promo code" @click="removePromoCodeFromRunner(promo)">
                    <svg viewBox="0 0 24 24" class="ado-btn-icon"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                  </button>
                </div>
              </div>
              <div v-else class="ado-empty-state">No promo codes assigned to this runner.</div>
            </div>
          </div>
          <div v-else class="ado-empty-state">Select a runner from Admin Tools to load actions.</div>
        </div>
        <div class="ado-dialog-footer">
          <button class="ado-ghost-btn" type="button" @click="backToAdminTools">
            <svg viewBox="0 0 24 24" class="ado-btn-icon"><path d="m15 18-6-6 6-6"/></svg>
            Back to Admin Tools
          </button>
          <button class="ado-ghost-btn" type="button" @click="$modal.hide('ado-runner-actions-dialog')">Close</button>
        </div>
      </div>
    </modal>

    <modal name="ado-result-dialog" class="ado-modal" :adaptive="true" :scrollable="true" height="auto" width="90%" :maxWidth="620">
      <div class="ado-dialog">
        <div class="ado-dialog-header">
          <span class="ado-icon-pill"><svg viewBox="0 0 24 24" class="ado-icon"><path d="M12 5v14"/><path d="M5 12h14"/></svg></span>
          <div>
            <h3 class="ado-dialog-title">{{ resultForm.id ? 'Edit Result' : 'Add Result' }}</h3>
            <p class="ado-dialog-sub">Save a runner result correction for the selected race.</p>
          </div>
          <button class="ado-close-btn" type="button" title="Close" @click="$modal.hide('ado-result-dialog')">
            <svg viewBox="0 0 24 24" class="ado-btn-icon"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </button>
        </div>
        <div class="ado-dialog-body">
          <label class="ado-field">
            <span class="ado-label">Race ID</span>
            <input v-model="resultForm.raceId" class="ado-input" type="number" list="ado-race-id-options">
            <datalist id="ado-race-id-options">
              <option v-for="race in races" :key="race.id" :value="race.id">{{ race.id }} - {{ race.name }}</option>
            </datalist>
          </label>
          <label class="ado-field"><span class="ado-label">Runner ID</span><input v-model="resultForm.runnerId" class="ado-input" type="number"></label>
          <label class="ado-field"><span class="ado-label">Distance</span><input v-model="resultForm.distance" class="ado-input" type="number" step="0.01"></label>
          <div class="ado-time-grid">
            <label class="ado-field"><span class="ado-label">Finish date</span><input v-model="resultForm.finishDate" class="ado-input" type="date"></label>
            <label class="ado-field"><span class="ado-label">Finish time</span><input v-model="resultForm.finishTime" class="ado-input" type="time" step="60"></label>
          </div>
          <div class="ado-time-preview">
            <span>Local time</span>
            <strong>{{ resultLocalPreview }}</strong>
            <small>UTC saved value: {{ resultUtcPreview }}</small>
          </div>
          <p v-if="dialogError" class="ado-error">{{ dialogError }}</p>
        </div>
        <div class="ado-dialog-footer">
          <button v-if="resultForm.id" class="ado-danger-btn" type="button" @click="removeRaceResult">Delete</button>
          <button class="ado-ghost-btn" type="button" @click="$modal.hide('ado-result-dialog')">Cancel</button>
          <button class="ado-primary-btn" type="button" @click="saveRaceResult">Save</button>
        </div>
      </div>
    </modal>

    <modal name="ado-bulk-medal-dialog" class="ado-modal" :adaptive="true" :scrollable="true" height="auto" width="90%" :maxWidth="620">
      <div class="ado-dialog">
        <div class="ado-dialog-header">
          <span class="ado-icon-pill"><svg viewBox="0 0 24 24" class="ado-icon"><path d="M8 21h8"/><path d="M12 17v4"/><path d="M7 4h10v5a5 5 0 0 1-10 0V4Z"/></svg></span>
          <div>
            <h3 class="ado-dialog-title">Bulk Medal</h3>
            <p class="ado-dialog-sub">Add or remove one medal for runner IDs in this race.</p>
          </div>
          <button class="ado-close-btn" type="button" title="Close" @click="$modal.hide('ado-bulk-medal-dialog')">
            <svg viewBox="0 0 24 24" class="ado-btn-icon"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </button>
        </div>
        <div class="ado-dialog-body">
          <div class="ado-medal-picker">
            <label class="ado-field">
              <span class="ado-label">Medal</span>
              <input v-model.trim="bulkMedal.medalSearch" class="ado-input" type="text" placeholder="Search medal name or ID">
            </label>
            <div v-if="selectedBulkMedal" class="ado-selected-medal">
              <span class="ado-selected-medal-name">{{ medalLabel(selectedBulkMedal) }}</span>
              <span class="ado-selected-medal-id">#{{ selectedBulkMedal.id }}</span>
            </div>
            <div v-if="medalOptionsLoading" class="ado-medal-grid">
              <span v-for="idx in 4" :key="idx" class="sk-shimmer ado-medal-option-skeleton"></span>
            </div>
            <div v-else-if="filteredBulkMedalOptions.length" class="ado-medal-grid">
              <button
                v-for="medal in filteredBulkMedalOptions"
                :key="medal.id"
                class="ado-medal-option"
                :class="{ 'ado-medal-option-selected': Number(bulkMedal.medalId) === Number(medal.id) }"
                type="button"
                @click="selectBulkMedal(medal)"
              >
                <span class="ado-medal-option-name">{{ medalLabel(medal) }}</span>
                <span class="ado-medal-option-id">#{{ medal.id }}</span>
              </button>
            </div>
            <div v-else class="ado-empty-state">No medals were returned for this race.</div>
            <p v-if="bulkMedalError" class="ado-error">{{ bulkMedalError }}</p>
          </div>
          <label class="ado-field"><span class="ado-label">Runner IDs</span><textarea v-model="bulkMedal.targetIds" class="ado-input ado-textarea" placeholder="comma or space separated IDs"></textarea></label>
          <label class="ado-field"><span class="ado-label">Action</span><select v-model.number="bulkMedal.action" class="ado-input"><option :value="0">Add</option><option :value="1">Remove</option></select></label>
        </div>
        <div class="ado-dialog-footer">
          <button class="ado-ghost-btn" type="button" @click="$modal.hide('ado-bulk-medal-dialog')">Cancel</button>
          <button class="ado-primary-btn" type="button" @click="performMedalBulk">Run Action</button>
        </div>
      </div>
    </modal>

    <modal name="ado-bulk-promo-dialog" class="ado-modal" :adaptive="true" :scrollable="true" height="auto" width="90%" :maxWidth="620">
      <div class="ado-dialog">
        <div class="ado-dialog-header">
          <span class="ado-icon-pill"><svg viewBox="0 0 24 24" class="ado-icon"><path d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7"/><path d="M2 7h20v5H2z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 1 1 0-5C11 2 12 7 12 7Z"/><path d="M12 7h4.5a2.5 2.5 0 1 0 0-5C13 2 12 7 12 7Z"/></svg></span>
          <div>
            <h3 class="ado-dialog-title">Bulk Promo Code</h3>
            <p class="ado-dialog-sub">Add or remove one promo code for multiple runner IDs.</p>
          </div>
          <button class="ado-close-btn" type="button" title="Close" @click="$modal.hide('ado-bulk-promo-dialog')">
            <svg viewBox="0 0 24 24" class="ado-btn-icon"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </button>
        </div>
        <div class="ado-dialog-body">
          <label class="ado-field"><span class="ado-label">Promo code id or code</span><input v-model="bulkPromo.promoCode" class="ado-input" type="text"></label>
          <label class="ado-field"><span class="ado-label">Runner IDs</span><textarea v-model="bulkPromo.targetIds" class="ado-input ado-textarea" placeholder="comma or space separated IDs"></textarea></label>
          <label class="ado-field"><span class="ado-label">Action</span><select v-model.number="bulkPromo.action" class="ado-input"><option :value="0">Add</option><option :value="1">Remove</option></select></label>
        </div>
        <div class="ado-dialog-footer">
          <button class="ado-ghost-btn" type="button" @click="$modal.hide('ado-bulk-promo-dialog')">Cancel</button>
          <button class="ado-primary-btn" type="button" @click="performPromoBulk">Run Action</button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import adminDataOperations from '@/services/adminDataOperations'

function sizeLabel(size) {
  if (size < 1024) {
    return `${size} B`
  }
  if (size < 1024 * 1024) {
    return `${Math.round(size / 1024)} KB`
  }
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

function dateToInput(date) {
  const safeDate = date instanceof Date ? date : new Date(date)
  const year = safeDate.getFullYear()
  const month = String(safeDate.getMonth() + 1).padStart(2, '0')
  const day = String(safeDate.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function timeToInput(date) {
  const safeDate = date instanceof Date ? date : new Date(date)
  const hours = String(safeDate.getHours()).padStart(2, '0')
  const minutes = String(safeDate.getMinutes()).padStart(2, '0')
  return `${hours}:${minutes}`
}

function inputToDate(value) {
  const parts = String(value || '').split('-').map(Number)
  if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
    return null
  }

  return new Date(parts[0], parts[1] - 1, parts[2])
}

function formatInputDate(value, options) {
  const date = inputToDate(value)
  if (!date) {
    return ''
  }

  return date.toLocaleDateString(undefined, options || { month: 'short', day: 'numeric', year: 'numeric' })
}

function dateTimeFromInputs(dateValue, timeValue) {
  const date = inputToDate(dateValue)
  if (!date) {
    return null
  }

  const timeParts = String(timeValue || '00:00').split(':').map(Number)
  date.setHours(Number.isFinite(timeParts[0]) ? timeParts[0] : 0)
  date.setMinutes(Number.isFinite(timeParts[1]) ? timeParts[1] : 0)
  date.setSeconds(0)
  date.setMilliseconds(0)
  return date
}

function addDays(date, amount) {
  const nextDate = new Date(date)
  nextDate.setDate(nextDate.getDate() + amount)
  return nextDate
}

function medalName(medal) {
  if (!medal) {
    return ''
  }
  return medal.badge || medal.textEnglish || medal.medalKey || `Medal ${medal.id}`
}

function sortMedals(medals) {
  return medals.slice().sort((first, second) => {
    const firstOrder = Number.isFinite(Number(first.orderMedal)) ? Number(first.orderMedal) : Number.MAX_SAFE_INTEGER
    const secondOrder = Number.isFinite(Number(second.orderMedal)) ? Number(second.orderMedal) : Number.MAX_SAFE_INTEGER
    if (firstOrder !== secondOrder) {
      return firstOrder - secondOrder
    }
    return medalName(first).localeCompare(medalName(second))
  })
}

export default {
  name: 'AdminDataOperations',
  data() {
    const now = new Date()
    const today = dateToInput(now)
    return {
      dateFilter: {
        selectedDate: today,
        rangeStart: today,
        rangeEnd: today,
        pendingRangeStart: today,
        pendingRangeEnd: today,
        calendarMonth: today,
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        day: now.getDate(),
      },
      todayInput: today,
      datePickerOpen: false,
      calendarWeekdays: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
      raceSearch: '',
      raceLoading: false,
      races: [],
      selectedRace: null,
      resultSearch: '',
      resultLoading: false,
      raceResults: [],
      resultPage: 0,
      resultPageModel: null,
      runnerMedalBoardMap: {},
      medalOptions: [],
      medalOptionsLoading: false,
      resultForm: {},
      bulkMedal: { action: 0, medalId: '', medalSearch: '', targetIds: '' },
      bulkMedalError: '',
      bulkPromo: { action: 0, promoCode: '', targetIds: '' },
      dialogError: '',
      runnerSearch: '',
      runnerLoading: false,
      runners: [],
      selectedRunner: null,
      runnerAnalytics: null,
      runnerPromoCode: '',
      uploadFiles: [],
      uploadLoading: false,
      uploadHistory: adminDataOperations.getUploadHistory(),
      skipAdminToolsClosedReset: false,
    }
  },
  computed: {
    selectedBulkMedal() {
      if (!this.bulkMedal.medalId) {
        return null
      }
      return this.medalOptions.find((medal) => Number(medal.id) === Number(this.bulkMedal.medalId)) || null
    },
    filteredBulkMedalOptions() {
      const search = String(this.bulkMedal.medalSearch || '').trim().toLowerCase()
      if (!search) {
        return this.medalOptions
      }
      return this.medalOptions.filter((medal) => {
        return [
          medal.id,
          medal.badge,
          medal.textEnglish,
          medal.medalKey,
        ].some((value) => String(value || '').toLowerCase().includes(search))
      })
    },
    dateRangeLabel() {
      if (this.dateFilter.rangeStart === this.dateFilter.rangeEnd) {
        return formatInputDate(this.dateFilter.rangeStart)
      }
      return `${formatInputDate(this.dateFilter.rangeStart, { month: 'short', day: 'numeric' })} - ${formatInputDate(this.dateFilter.rangeEnd)}`
    },
    pendingDateRangeLabel() {
      if (this.dateFilter.pendingRangeStart === this.dateFilter.pendingRangeEnd) {
        return formatInputDate(this.dateFilter.pendingRangeStart)
      }
      return `${formatInputDate(this.dateFilter.pendingRangeStart, { month: 'short', day: 'numeric' })} - ${formatInputDate(this.dateFilter.pendingRangeEnd)}`
    },
    calendarMonthLabel() {
      const month = inputToDate(this.dateFilter.calendarMonth)
      return month ? month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) : ''
    },
    calendarDays() {
      const month = inputToDate(this.dateFilter.calendarMonth)
      if (!month) {
        return []
      }

      const firstDay = new Date(month.getFullYear(), month.getMonth(), 1)
      const gridStart = addDays(firstDay, -firstDay.getDay())
      return Array.from({ length: 42 }, (item, index) => {
        const day = addDays(gridStart, index)
        const value = dateToInput(day)
        return {
          key: `${value}-${index}`,
          value,
          label: day.getDate(),
          inMonth: day.getMonth() === month.getMonth(),
        }
      })
    },
    resultLocalPreview() {
      const date = dateTimeFromInputs(this.resultForm.finishDate, this.resultForm.finishTime)
      if (!date) {
        return '-'
      }
      return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    },
    resultUtcPreview() {
      const date = dateTimeFromInputs(this.resultForm.finishDate, this.resultForm.finishTime)
      return date ? date.toISOString() : '-'
    },
  },
  created() {
    if (!this.$isAdminUser()) {
      this.$router.replace({ path: '/admin/live-run' })
      return
    }

    this.loadSessionsOfDay()
    if (this.$route.query && this.$route.query.runner) {
      this.runnerSearch = this.$route.query.runner
      this.loadRunnerDetail(this.$route.query.runner)
    }
  },
  methods: {
    notifySuccess(message) {
      if (this.$toast && this.$toast.success) {
        this.$toast.success(message)
      }
    },
    notifyError(message) {
      if (this.$toast && this.$toast.error) {
        this.$toast.error(message)
      }
    },
    resetAdminToolsState(keepRunnerContext = false) {
      this.runnerSearch = ''
      this.runnerLoading = false
      this.runners = []
      if (!keepRunnerContext) {
        this.selectedRunner = null
        this.runnerAnalytics = null
        this.runnerPromoCode = ''
      }
      this.uploadFiles = []
      this.uploadLoading = false
      if (this.$refs.mp3Input) {
        this.$refs.mp3Input.value = ''
      }
    },
    handleAdminToolsClosed() {
      if (this.skipAdminToolsClosedReset) {
        this.skipAdminToolsClosedReset = false
        return
      }

      this.resetAdminToolsState()
    },
    openAdminTools() {
      this.datePickerOpen = false
      this.resetAdminToolsState()
      this.$modal.show('ado-admin-tools-dialog')
    },
    closeAdminTools(keepRunnerContext = false) {
      if (keepRunnerContext) {
        this.skipAdminToolsClosedReset = true
      }
      this.$modal.hide('ado-admin-tools-dialog')
      this.resetAdminToolsState(keepRunnerContext)
    },
    async openRunnerActions(runnerId) {
      this.selectedRunner = null
      this.runnerAnalytics = null
      this.runnerPromoCode = ''
      this.$modal.show('ado-runner-actions-dialog')
      await this.loadRunnerDetail(runnerId)
    },
    backToAdminTools() {
      this.$modal.hide('ado-runner-actions-dialog')
    },
    formatDate(value) {
      if (!value) {
        return ''
      }
      return new Date(value).toLocaleString()
    },
    responseData(response) {
      return response && response.body ? response.body : response.data
    },
    syncDatePartsFromSelectedDate() {
      const date = inputToDate(this.dateFilter.selectedDate)
      if (!date) {
        return
      }

      this.dateFilter.year = date.getFullYear()
      this.dateFilter.month = date.getMonth() + 1
      this.dateFilter.day = date.getDate()
      this.dateFilter.rangeStart = this.dateFilter.selectedDate
      this.dateFilter.rangeEnd = this.dateFilter.selectedDate
    },
    toggleDatePicker() {
      this.datePickerOpen = !this.datePickerOpen
      if (this.datePickerOpen) {
        this.dateFilter.pendingRangeStart = this.dateFilter.rangeStart
        this.dateFilter.pendingRangeEnd = this.dateFilter.rangeEnd
        this.dateFilter.calendarMonth = this.dateFilter.rangeEnd || this.dateFilter.selectedDate
      }
    },
    shiftCalendarMonth(amount) {
      const month = inputToDate(this.dateFilter.calendarMonth) || new Date()
      const nextMonth = new Date(month.getFullYear(), month.getMonth() + amount, 1)
      this.dateFilter.calendarMonth = dateToInput(nextMonth)
    },
    selectCalendarDay(value) {
      const start = this.dateFilter.pendingRangeStart
      const end = this.dateFilter.pendingRangeEnd
      if (!start || (start && end && start !== end)) {
        this.dateFilter.pendingRangeStart = value
        this.dateFilter.pendingRangeEnd = value
        return
      }

      if (value < start) {
        this.dateFilter.pendingRangeStart = value
        this.dateFilter.pendingRangeEnd = start
        return
      }

      this.dateFilter.pendingRangeEnd = value
    },
    isCalendarBoundary(value) {
      return value === this.dateFilter.pendingRangeStart || value === this.dateFilter.pendingRangeEnd
    },
    isCalendarInRange(value) {
      const start = this.dateFilter.pendingRangeStart
      const end = this.dateFilter.pendingRangeEnd
      return start && end && value > start && value < end
    },
    setDatePreset(preset) {
      const today = new Date()
      if (preset === 'yesterday') {
        const yesterday = dateToInput(addDays(today, -1))
        this.dateFilter.pendingRangeStart = yesterday
        this.dateFilter.pendingRangeEnd = yesterday
        this.dateFilter.calendarMonth = yesterday
        return
      }

      if (preset === 'last7') {
        this.dateFilter.pendingRangeStart = dateToInput(addDays(today, -6))
        this.dateFilter.pendingRangeEnd = dateToInput(today)
        this.dateFilter.calendarMonth = dateToInput(today)
        return
      }

      const current = dateToInput(today)
      this.dateFilter.pendingRangeStart = current
      this.dateFilter.pendingRangeEnd = current
      this.dateFilter.calendarMonth = current
    },
    async applyDateRange() {
      this.dateFilter.rangeStart = this.dateFilter.pendingRangeStart
      this.dateFilter.rangeEnd = this.dateFilter.pendingRangeEnd
      this.dateFilter.selectedDate = this.dateFilter.rangeEnd
      const selected = inputToDate(this.dateFilter.selectedDate)
      if (selected) {
        this.dateFilter.year = selected.getFullYear()
        this.dateFilter.month = selected.getMonth() + 1
        this.dateFilter.day = selected.getDate()
      }
      this.datePickerOpen = false
      if (this.dateFilter.rangeStart === this.dateFilter.rangeEnd) {
        await this.loadSessionsOfDay()
      } else {
        await this.loadSessionsOfRange()
      }
    },
    async loadSessionsOfDay() {
      this.syncDatePartsFromSelectedDate()
      this.raceLoading = true
      try {
        const response = await adminDataOperations.getSessionsOfDay(this.dateFilter.year, this.dateFilter.month, this.dateFilter.day)
        const data = this.responseData(response)
        this.races = data && data.content ? data.content : []
      } catch (error) {
        this.notifyError('Error loading race sessions')
      } finally {
        this.raceLoading = false
      }
    },
    async loadSessionsOfRange() {
      const startDate = inputToDate(this.dateFilter.rangeStart)
      const endDate = inputToDate(this.dateFilter.rangeEnd)
      if (!startDate || !endDate || startDate > endDate) {
        this.notifyError('Select a valid date range')
        return
      }

      this.raceLoading = true
      try {
        const racesById = {}
        for (let date = new Date(startDate); date <= endDate; date = addDays(date, 1)) {
          const response = await adminDataOperations.getSessionsOfDay(date.getFullYear(), date.getMonth() + 1, date.getDate())
          const data = this.responseData(response)
          const rows = data && data.content ? data.content : []
          rows.forEach((race) => {
            racesById[race.id] = race
          })
        }
        this.races = Object.keys(racesById).map((id) => racesById[id])
      } catch (error) {
        this.notifyError('Error loading race range')
      } finally {
        this.raceLoading = false
      }
    },
    async searchRaces() {
      this.datePickerOpen = false
      this.raceLoading = true
      try {
        const response = await adminDataOperations.searchRaces(this.raceSearch, 0)
        const data = this.responseData(response)
        this.races = data && data.content ? data.content : []
      } catch (error) {
        this.notifyError('Error searching races')
      } finally {
        this.raceLoading = false
      }
    },
    async selectRace(race) {
      this.datePickerOpen = false
      this.selectedRace = race
      this.resultSearch = ''
      await this.loadRaceResults(true)
    },
    async loadRaceResults(clear) {
      if (!this.selectedRace) {
        return
      }

      if (clear) {
        this.resultPage = 0
        this.raceResults = []
        this.runnerMedalBoardMap = {}
        this.medalOptions = []
      }

      this.resultLoading = true
      try {
        const response = await adminDataOperations.getRaceResultsPage(this.selectedRace.id, this.resultPage, 20, this.resultSearch)
        const data = this.responseData(response)
        this.resultPageModel = data
        const rows = data && data.content ? data.content : []
        this.raceResults = clear ? rows : this.raceResults.concat(rows)
        await this.loadMedalsForRace()
      } catch (error) {
        this.notifyError('Error loading race results')
      } finally {
        this.resultLoading = false
      }
    },
    async loadNextResultPage() {
      this.resultPage += 1
      await this.loadRaceResults(false)
    },
    clearResultFilter() {
      this.resultSearch = ''
      this.loadRaceResults(true)
    },
    async loadMedalsForRace() {
      if (!this.selectedRace) {
        return
      }

      this.medalOptionsLoading = true
      try {
        const medalsResponse = await adminDataOperations.getMedalsGivenInRace(this.selectedRace.id)
        const medals = sortMedals(this.responseData(medalsResponse) || [])
        this.medalOptions = medals
        const medalIdMap = {}
        medals.forEach((medal) => {
          medalIdMap[medal.id] = medal
        })

        const mapResponse = await adminDataOperations.getMedalBoardMap(this.selectedRace.id)
        const medalMap = this.responseData(mapResponse) || []
        const runnerMap = {}
        medalMap.forEach((entry) => {
          const medal = medalIdMap[entry.medalId]
          ;(entry.runnerIdList || []).forEach((runnerId) => {
            if (!runnerMap[runnerId]) {
              runnerMap[runnerId] = []
            }
            runnerMap[runnerId].push({ medal })
          })
        })
        this.runnerMedalBoardMap = runnerMap
      } finally {
        this.medalOptionsLoading = false
      }
    },
    medalsForRunner(runnerId) {
      return this.runnerMedalBoardMap[runnerId] || []
    },
    medalLabel(medal) {
      return medalName(medal)
    },
    openResultDialog(result) {
      this.dialogError = ''
      const fallbackFinishDate = this.selectedRace && (this.selectedRace.rawFinishDate || this.selectedRace.rawStartDate || this.selectedRace.rawDate)
      this.resultForm = {
        id: result && result.id,
        raceId: this.selectedRace ? this.selectedRace.id : '',
        runnerId: result && result.runner ? result.runner.id : '',
        distance: result && result.distance ? result.distance : '',
        finishDate: result && result.rawFinishDate ? dateToInput(result.rawFinishDate) : (fallbackFinishDate ? dateToInput(fallbackFinishDate) : dateToInput(new Date())),
        finishTime: result && result.rawFinishDate ? timeToInput(result.rawFinishDate) : (fallbackFinishDate ? timeToInput(fallbackFinishDate) : timeToInput(new Date())),
      }
      this.$modal.show('ado-result-dialog')
    },
    async saveRaceResult() {
      if (!this.resultForm.raceId || !this.resultForm.runnerId || !this.resultForm.distance || !this.resultForm.finishDate || !this.resultForm.finishTime) {
        this.dialogError = 'Race, runner, distance, finish date, and finish time are required.'
        return
      }

      const finishDate = dateTimeFromInputs(this.resultForm.finishDate, this.resultForm.finishTime)
      if (!finishDate) {
        this.dialogError = 'Select a valid finish date and time.'
        return
      }

      const payload = {
        runnerId: Number(this.resultForm.runnerId),
        distance: Number(this.resultForm.distance),
        finishDate: finishDate.toISOString(),
      }

      try {
        if (this.resultForm.id) {
          await adminDataOperations.updateRaceResult(this.resultForm.id, payload)
        } else {
          await adminDataOperations.addRaceResult(Number(this.resultForm.raceId), payload)
        }
        this.$modal.hide('ado-result-dialog')
        this.notifySuccess('Result saved')
        if (this.selectedRace && Number(this.resultForm.raceId) === Number(this.selectedRace.id)) {
          await this.loadRaceResults(true)
        }
      } catch (error) {
        this.dialogError = 'Error saving race result.'
      }
    },
    async removeRaceResult() {
      if (!window.confirm('Remove this race result?')) {
        return
      }

      try {
        await adminDataOperations.removeRaceResult(this.resultForm.id)
        this.$modal.hide('ado-result-dialog')
        this.notifySuccess('Result removed')
        await this.loadRaceResults(true)
      } catch (error) {
        this.dialogError = 'Error removing race result.'
      }
    },
    async openBulkMedalDialog(runnerId, medal, action = 0) {
      this.bulkMedalError = ''
      this.bulkMedal = {
        action,
        medalId: medal && medal.id ? medal.id : '',
        medalSearch: medal ? medalName(medal) : '',
        targetIds: runnerId ? String(runnerId) : '',
      }
      if (this.selectedRace && this.medalOptions.length === 0) {
        await this.loadMedalsForRace()
      }
      this.$modal.show('ado-bulk-medal-dialog')
    },
    selectBulkMedal(medal) {
      this.bulkMedal.medalId = medal.id
      this.bulkMedal.medalSearch = medalName(medal)
      this.bulkMedalError = ''
    },
    async performMedalBulk() {
      if (!this.bulkMedal.medalId) {
        this.bulkMedalError = 'Select a medal before running the action.'
        return
      }
      if (!this.bulkMedal.targetIds) {
        this.bulkMedalError = 'Enter at least one runner ID.'
        return
      }

      try {
        await adminDataOperations.performMedalBulk(this.selectedRace.id, this.bulkMedal)
        this.$modal.hide('ado-bulk-medal-dialog')
        this.notifySuccess('Medal action complete')
        await this.loadMedalsForRace()
      } catch (error) {
        this.notifyError('Error performing medal action')
      }
    },
    openBulkPromoDialog() {
      this.bulkPromo = { action: 0, promoCode: '', targetIds: '' }
      this.$modal.show('ado-bulk-promo-dialog')
    },
    async performPromoBulk() {
      try {
        await adminDataOperations.performPromoCodeBulk(this.bulkPromo)
        this.$modal.hide('ado-bulk-promo-dialog')
        this.notifySuccess('Promo-code action complete')
      } catch (error) {
        this.notifyError('Error performing promo-code action')
      }
    },
    async searchRunners() {
      this.runnerLoading = true
      try {
        const response = await adminDataOperations.searchRunners(this.runnerSearch, 0)
        const data = this.responseData(response)
        this.runners = data && data.content ? data.content : []
      } catch (error) {
        this.notifyError('Error searching runners')
      } finally {
        this.runnerLoading = false
      }
    },
    async loadRunnerDetail(runnerId) {
      this.runnerLoading = true
      try {
        const runnerResponse = await adminDataOperations.getRunner(runnerId)
        this.selectedRunner = this.responseData(runnerResponse)
        const analyticsResponse = await adminDataOperations.getRunnerAnalytics(runnerId)
        this.runnerAnalytics = this.responseData(analyticsResponse)
      } catch (error) {
        this.notifyError('Error loading runner detail')
      } finally {
        this.runnerLoading = false
      }
    },
    async resetRunnerDistance() {
      if (!this.selectedRunner || !window.confirm('Reset total distance from performed results?')) {
        return
      }

      await adminDataOperations.updateRunnerTotalDistanceFromResults(this.selectedRunner.id)
      await this.loadRunnerDetail(this.selectedRunner.id)
      this.notifySuccess('Runner distance reset')
    },
    async resetRunnerPassword() {
      if (!this.selectedRunner || !this.selectedRunner.user || !window.confirm('Reset runner password?')) {
        return
      }

      await adminDataOperations.resetRunnerPassword(this.selectedRunner.user.id)
      this.notifySuccess('Runner password reset')
    },
    async addPromoCodeToRunner() {
      if (!this.selectedRunner || !this.runnerPromoCode) {
        return
      }

      await adminDataOperations.addPromoCodeToRunner(this.selectedRunner.id, this.runnerPromoCode)
      this.runnerPromoCode = ''
      await this.loadRunnerDetail(this.selectedRunner.id)
      this.notifySuccess('Promo code added')
    },
    async removePromoCodeFromRunner(promo) {
      if (!window.confirm(`Remove promo code ${promo.code}?`)) {
        return
      }

      await adminDataOperations.removePromoCodeFromRunner(this.selectedRunner.id, promo.id)
      await this.loadRunnerDetail(this.selectedRunner.id)
      this.notifySuccess('Promo code removed')
    },
    handleFiles() {
      const files = this.$refs.mp3Input.files || []
      this.uploadFiles = Array.prototype.map.call(files, (file, index) => {
        const extensionIndex = file.name.lastIndexOf('.')
        const extension = extensionIndex >= 0 ? file.name.slice(extensionIndex).toLowerCase() : ''
        const name = extensionIndex >= 0 ? file.name.slice(0, extensionIndex) : file.name
        return {
          _key: `${file.name}-${index}`,
          file,
          name,
          extension,
          size: file.size,
          sizeLabel: sizeLabel(file.size),
          progress: 0,
        }
      })
    },
    async uploadAllFiles() {
      this.uploadLoading = true
      try {
        for (let index = 0; index < this.uploadFiles.length; index += 1) {
          const fileModel = this.uploadFiles[index]
          const response = await adminDataOperations.uploadMp3File(fileModel, (progress) => {
            fileModel.progress = progress
          })
          if (response && response.s3Url) {
            this.uploadHistory = adminDataOperations.addUploadHistoryEntry({
              name: `${fileModel.name}${fileModel.extension}`,
              url: response.s3Url,
            })
          }
        }
        this.notifySuccess('Upload finished')
      } catch (error) {
        this.notifyError('Upload failed')
      } finally {
        this.uploadLoading = false
      }
    },
    clearUploadHistory() {
      if (window.confirm('Clear upload history?')) {
        this.uploadHistory = adminDataOperations.clearUploadHistory()
      }
    },
    copyUrl(url) {
      navigator.clipboard.writeText(url)
      this.notifySuccess('URL copied')
    },
  },
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap');

.ado-modal .v--modal-overlay {
  background: rgba(10, 10, 30, 0.38) !important;
  backdrop-filter: blur(14px) saturate(160%);
  -webkit-backdrop-filter: blur(14px) saturate(160%);
}

.ado-modal .v--modal {
  border-radius: 16px !important;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.18) !important;
  overflow: hidden !important;
}
</style>

<style scoped>
@keyframes sk-shimmer {
  0% { background-position: -600px 0; }
  100% { background-position: 600px 0; }
}

.ado-page {
  padding: 1.5rem 2rem 5rem;
  background: #fff;
}

.pg-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f5;
  margin-bottom: 1.5rem;
}

.pg-title {
  font-family: 'Inter', sans-serif;
  font-size: 34px;
  font-weight: 900;
  letter-spacing: -0.04em;
  color: #1a1a2e;
  line-height: 1.1;
  margin: 0;
}

.pg-title-sub {
  font-size: 13px;
  color: #8a8a9a;
  font-weight: 400;
  margin-top: 5px;
}

.ado-command-strip {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) 140px minmax(0, 1fr);
  gap: 10px;
  align-items: stretch;
  margin-bottom: 1rem;
}

.ado-command-item {
  border: 1px solid #e8e8ed;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.ado-command-item {
  min-height: 68px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
}

.ado-command-item-active {
  border-color: #d9d5ff;
  background: #fbfaff;
}

.ado-command-kicker {
  color: #8a8a9a;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.ado-command-value {
  color: #1a1a2e;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.25;
}

.ado-workbench {
  display: grid;
  grid-template-columns: minmax(320px, 0.42fr) minmax(0, 1fr);
  gap: 1rem;
  align-items: start;
}

.ado-tools-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.3fr) minmax(340px, 0.7fr);
  gap: 1rem;
}

.ado-tool-panel {
  border: 1px solid #e8e8ed;
  border-radius: 14px;
  padding: 1rem;
  background: #fff;
}

.ado-tools-dialog {
  background: #fff;
}

.ado-card {
  border: 1px solid #e8e8ed;
  border-radius: 14px;
  padding: 1.5rem 1.75rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  background: #fff;
}

.ado-race-card {
  position: sticky;
  top: 12px;
}

.ado-card-header,
.ado-header-left,
.ado-actions,
.ado-toolbar,
.ado-dialog-header,
.ado-dialog-footer,
.ado-promo-row,
.ado-upload-row {
  display: flex;
  align-items: center;
}

.ado-card-header {
  gap: 12px;
  margin-bottom: 1.1rem;
}

.ado-card-header-spread {
  justify-content: space-between;
}

.ado-card-header-compact {
  align-items: flex-start;
}

.ado-header-left {
  gap: 12px;
  min-width: 0;
}

.ado-icon-pill {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0effe;
  color: #5b4fcf;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.ado-icon,
.ado-btn-icon {
  width: 16px;
  height: 16px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.ado-btn-icon {
  width: 13px;
  height: 13px;
}

.ado-card-title,
.ado-dialog-title {
  color: #1a1a2e;
  margin: 0;
  font-weight: 700;
}

.ado-card-title {
  font-size: 16px;
}

.ado-card-sub,
.ado-dialog-sub,
.ado-muted {
  color: #8a8a9a;
  font-size: 12px;
}

.ado-card-sub,
.ado-dialog-sub {
  margin: 3px 0 0;
}

.ado-filter-panel {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 1rem;
}

.ado-date-chip {
  border: 1px solid #e8e8ed;
  border-radius: 8px;
  background: #fff;
  color: #5b4fcf;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  height: 32px;
}

.ado-date-chip:hover {
  background: #f0effe;
  border-color: #5b4fcf;
}

.ado-date-range-control {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.ado-range-button {
  border: 1px solid #e8e8ed;
  border-radius: 10px;
  background: #fafafa;
  color: #1a1a2e;
  min-height: 54px;
  padding: 8px 11px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  cursor: pointer;
  text-align: left;
}

.ado-range-button:hover {
  border-color: #5b4fcf;
  background: #fbfaff;
}

.ado-range-button strong,
.ado-range-button small {
  display: block;
}

.ado-range-button strong {
  font-size: 14px;
  color: #1a1a2e;
}

.ado-range-button small {
  color: #8a8a9a;
  font-size: 11px;
  font-weight: 700;
  margin-top: 2px;
  text-transform: uppercase;
}

.ado-calendar-popover {
  border: 1px solid #e8e8ed;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(26, 26, 46, 0.12);
  background: #fff;
  padding: 10px;
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 2000;
}

.ado-calendar-presets,
.ado-calendar-head,
.ado-calendar-footer {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ado-calendar-presets {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-bottom: 8px;
}

.ado-calendar-head {
  justify-content: space-between;
  margin-bottom: 8px;
  color: #1a1a2e;
}

.ado-calendar-weekdays,
.ado-calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
}

.ado-calendar-weekdays {
  color: #8a8a9a;
  font-size: 11px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 4px;
}

.ado-calendar-grid {
  gap: 3px;
}

.ado-calendar-day {
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: #1a1a2e;
  cursor: pointer;
  height: 27px;
  font-size: 12px;
  font-weight: 700;
}

.ado-calendar-day:hover {
  border-color: #5b4fcf;
}

.ado-calendar-day-muted {
  color: #c0c0cc;
}

.ado-calendar-day-range {
  background: #f0effe;
  color: #413b94;
}

.ado-calendar-day-selected {
  background: #5b4fcf;
  color: #fff;
}

.ado-calendar-day-today {
  border-color: #d9d5ff;
}

.ado-calendar-footer {
  justify-content: space-between;
  border-top: 1px solid #f0f0f5;
  color: #8a8a9a;
  font-size: 12px;
  font-weight: 700;
  margin-top: 8px;
  padding-top: 8px;
}

.ado-toolbar {
  gap: 10px;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.ado-actions {
  gap: 10px;
  row-gap: 10px;
}

.ado-toolbar-stack {
  align-items: flex-end;
}

.ado-result-toolbar {
  padding: 10px;
  border: 1px solid #f0f0f5;
  border-radius: 10px;
  background: #fafafa;
}

.ado-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex: 0 1 auto;
}

.ado-field-small {
  flex: 0 0 120px;
}

.ado-toolbar .ado-field {
  flex: 1 1 180px;
}

.ado-label {
  font-size: 12px;
  font-weight: 600;
  color: #1a1a2e;
}

.ado-input {
  background: #fafafa;
  border: 1px solid #e8e8ed;
  border-radius: 8px;
  min-height: 36px;
  padding: 0 10px;
  color: #1a1a2e;
  font-size: 13px;
  outline: none;
}

.ado-input:focus {
  border-color: #5b4fcf;
  box-shadow: 0 0 0 3px rgba(91, 79, 207, 0.1);
}

.ado-textarea {
  min-height: 84px;
  padding-top: 10px;
}

.ado-primary-btn,
.ado-ghost-btn,
.ado-danger-btn {
  height: 36px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  padding: 0 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  white-space: nowrap;
}

.ado-primary-btn {
  background: #5b4fcf;
  color: #fff;
  border: none;
}

.ado-primary-btn:hover {
  background: #4338ca;
}

.ado-primary-btn:disabled,
.ado-ghost-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.ado-ghost-btn {
  border: 1.5px solid #5b4fcf;
  background: #fff;
  color: #5b4fcf;
}

.ado-fluid-btn {
  width: 100%;
}

.ado-danger-btn {
  border: none;
  background: #c0392b;
  color: #fff;
  margin-right: auto;
}

.ado-icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: 1px solid #e8e8ed;
  background: #fff;
  color: #5b4fcf;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.ado-icon-btn:hover {
  background: #f0effe;
}

.ado-table-wrap {
  overflow-x: auto;
  border: 1px solid #f0f0f5;
  border-radius: 10px;
}

.ado-table {
  width: 100%;
  border-collapse: collapse;
}

.ado-th,
.ado-td {
  border-bottom: 1px solid #f0f0f5;
  padding: 11px 12px;
  text-align: left;
  font-size: 13px;
  vertical-align: middle;
}

.ado-th {
  background: #fafafa;
  color: #1a1a2e;
  font-weight: 700;
}

.ado-result-row:hover {
  background: #fbfaff;
}

.ado-rank-cell {
  color: #1a1a2e;
  font-weight: 700;
}

.ado-link-btn,
.ado-mini-link,
.ado-mini-action {
  border: none;
  background: transparent;
  color: #5b4fcf;
  font-weight: 600;
  padding: 0;
  cursor: pointer;
  text-decoration: none;
}

.ado-mini-action {
  margin-left: 8px;
  font-size: 12px;
}

.ado-empty {
  color: #8a8a9a;
  text-align: center;
  padding: 28px 12px;
  font-size: 13px;
}

.ado-empty-state {
  border: 1px dashed #d0d0e0;
  border-radius: 10px;
  color: #8a8a9a;
  font-size: 13px;
  padding: 18px;
  text-align: center;
  background: #fafafa;
}

.ado-medal-chip {
  display: inline-flex;
  border-radius: 7px;
  background: #f0effe;
  color: #413b94;
  padding: 4px 8px;
  margin: 2px;
  font-size: 12px;
  font-weight: 600;
}

.ado-medal-chip-button {
  border: 1px solid transparent;
  cursor: pointer;
}

.ado-medal-chip-button:hover {
  border-color: #5b4fcf;
  background: #e6e2ff;
}

.ado-medal-picker {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ado-selected-medal {
  border: 1px solid #d9d5ff;
  border-radius: 9px;
  background: #fbfaff;
  min-height: 40px;
  padding: 8px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.ado-selected-medal-name {
  color: #1a1a2e;
  font-size: 13px;
  font-weight: 700;
}

.ado-selected-medal-id {
  color: #8a8a9a;
  font-size: 12px;
  font-weight: 700;
}

.ado-medal-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  max-height: 190px;
  overflow-y: auto;
  padding-right: 2px;
}

.ado-medal-option {
  border: 1px solid #e8e8ed;
  border-radius: 9px;
  background: #fff;
  color: #1a1a2e;
  min-height: 42px;
  padding: 8px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
  text-align: left;
}

.ado-medal-option:hover,
.ado-medal-option-selected {
  border-color: #5b4fcf;
  background: #fbfaff;
}

.ado-medal-option-name {
  font-size: 13px;
  font-weight: 700;
}

.ado-medal-option-id {
  color: #8a8a9a;
  font-size: 12px;
  font-weight: 700;
}

.ado-medal-option-skeleton {
  min-height: 42px;
}

.ado-time-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 150px;
  gap: 10px;
}

.ado-time-preview {
  border: 1px solid #e8e8ed;
  border-radius: 10px;
  background: #fbfaff;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.ado-time-preview span {
  color: #8a8a9a;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.ado-time-preview strong {
  color: #1a1a2e;
  font-size: 14px;
  font-weight: 700;
}

.ado-time-preview small {
  color: #8a8a9a;
  font-size: 12px;
}

.ado-race-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 590px;
  overflow-y: auto;
  padding-right: 2px;
}

.ado-race-option {
  width: 100%;
  border: 1px solid #e8e8ed;
  border-radius: 10px;
  background: #fff;
  padding: 12px;
  cursor: pointer;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.ado-race-option:hover,
.ado-race-option-selected {
  border-color: #5b4fcf;
  background: #fbfaff;
}

.ado-race-main {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.ado-race-title {
  color: #1a1a2e;
  font-size: 13px;
  line-height: 1.3;
}

.ado-race-meta,
.ado-race-foot {
  color: #8a8a9a;
  font-size: 12px;
}

.ado-race-stats,
.ado-race-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.ado-stat-pill {
  border-radius: 999px;
  background: #f0effe;
  color: #413b94;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 700;
}

.ado-selected-race-panel {
  border: 1px solid #e8e8ed;
  border-radius: 10px;
  background: #fbfaff;
  display: grid;
  grid-template-columns: 120px 100px minmax(0, 1fr);
  gap: 10px;
  padding: 10px 12px;
  margin-bottom: 1rem;
}

.ado-race-summary-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.ado-skeleton-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ado-skeleton-row {
  display: grid;
  grid-template-columns: 180px minmax(160px, 1fr) 90px;
  gap: 12px;
  align-items: center;
  border-bottom: 1px solid #f0f0f5;
  padding: 10px 0;
}

.sk-shimmer {
  background: linear-gradient(90deg, #f0f0f5 25%, #e4e4ef 50%, #f0f0f5 75%);
  background-size: 600px 100%;
  animation: sk-shimmer 1.4s ease-in-out infinite;
  border-radius: 6px;
}

.ado-sk-title,
.ado-sk-line,
.ado-sk-pill {
  height: 16px;
}

.ado-sk-pill {
  border-radius: 999px;
}

.ado-load-more {
  margin-top: 1rem;
}

.ado-runner-row,
.ado-history {
  border-top: 1px solid #f0f0f5;
  padding-top: 10px;
  margin-top: 10px;
}

.ado-runner-select {
  width: 100%;
  border: 1px solid #e8e8ed;
  border-radius: 10px;
  background: #fff;
  color: #1a1a2e;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 11px 12px;
  text-align: left;
}

.ado-runner-select:hover {
  border-color: #5b4fcf;
  background: #fbfaff;
}

.ado-runner-select strong,
.ado-runner-select small {
  display: block;
}

.ado-runner-select strong {
  color: #5b4fcf;
  font-size: 13px;
}

.ado-runner-select small {
  color: #8a8a9a;
  font-size: 11px;
  font-weight: 600;
  margin-top: 3px;
}

.ado-runner-actions-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.ado-detail-title {
  color: #1a1a2e;
  border: 1px solid #e8e8ed;
  border-radius: 10px;
  background: #fafafa;
  padding: 12px;
  font-weight: 700;
}

.ado-kpi-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.ado-kpi {
  border: 1px solid #e8e8ed;
  border-radius: 8px;
  background: #fff;
  padding: 10px;
}

.ado-kpi-label,
.ado-kpi-value {
  display: block;
}

.ado-kpi-label {
  color: #8a8a9a;
  font-size: 12px;
}

.ado-kpi-value {
  color: #1a1a2e;
  font-size: 16px;
  margin-top: 3px;
}

.ado-runner-action-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 2px;
}

.ado-promo-manager {
  border: 1px solid #e8e8ed;
  border-radius: 12px;
  background: #fff;
  padding: 12px;
}

.ado-promo-manager .ado-toolbar {
  margin-bottom: 14px;
}

.ado-section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: #1a1a2e;
  margin-bottom: 12px;
}

.ado-section-heading strong {
  font-size: 13px;
}

.ado-section-heading span {
  color: #8a8a9a;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

.ado-promo-list {
  border-top: 1px solid #f0f0f5;
}

.ado-promo-row,
.ado-upload-row {
  justify-content: space-between;
  gap: 10px;
  border-bottom: 1px solid #f0f0f5;
  padding: 8px 0;
  font-size: 13px;
}

.ado-file-zone {
  border: 1.5px dashed #d0d0e0;
  border-radius: 9px;
  min-height: 92px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  color: #5b4fcf;
  font-weight: 600;
  cursor: pointer;
  background: #fafafa;
  margin-bottom: 14px;
}

.ado-file-zone:hover {
  border-color: #5b4fcf;
  background: #fbfaff;
}

.ado-file-zone-title {
  font-size: 14px;
  font-weight: 700;
}

.ado-file-zone-sub {
  color: #8a8a9a;
  font-size: 12px;
  font-weight: 600;
}

.ado-file-input {
  display: none;
}

.ado-dialog-header {
  gap: 12px;
  padding: 1rem 1.75rem;
  border-bottom: 1px solid #f0f0f5;
}

.ado-dialog-title {
  font-size: 16px;
}

.ado-close-btn {
  width: 30px;
  height: 30px;
  margin-left: auto;
  border: 1px solid #e8e8ed;
  border-radius: 999px;
  background: #fff;
  color: #8a8a9a;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.ado-close-btn:hover {
  color: #c0392b;
}

.ado-dialog-body {
  padding: 1.5rem 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
}

.ado-dialog-body .ado-field {
  flex: 0 0 auto;
}

.ado-dialog-footer {
  border-top: 1px solid #f0f0f5;
  padding: 1rem 1.75rem 1.5rem;
  justify-content: flex-end;
  gap: 10px;
}

.ado-error {
  color: #c0392b;
  font-size: 12px;
  margin: 0;
}

@media (max-width: 900px) {
  .ado-page {
    padding: 1rem 1rem 4rem;
  }

  .ado-command-strip,
  .ado-workbench,
  .ado-tools-grid,
  .ado-kpi-grid {
    grid-template-columns: 1fr;
  }

  .ado-race-card {
    position: static;
  }

  .ado-selected-race-panel,
  .ado-calendar-presets,
  .ado-time-grid,
  .ado-medal-grid {
    grid-template-columns: 1fr;
  }

  .ado-calendar-popover {
    position: static;
    margin-top: 8px;
  }

  .ado-card-header-spread {
    align-items: flex-start;
    flex-direction: column;
  }

  .ado-results-actions,
  .ado-actions {
    flex-wrap: wrap;
    justify-content: flex-start;
  }
}
</style>



// WEBPACK FOOTER //
// AdminDataOperations.vue?a40decd8